import { createSelector } from 'reselect';

import { convertListToObjectBy } from 'utils/fn';
import { getBodyData } from 'store/selectors';
import { getComparedProductIds } from 'store/common/compare/selectors';
import { getBasketProductIds } from 'store/common/basket/selectors';
import { RequestFiredStatusList, REQUEST_SUCCESS } from 'store/constants';

const getCrossSellProductsData = (state) => getBodyData(state).crossSellProducts;

export const getCrossSellProductsHeadline = (state) => getCrossSellProductsData(state).headline;
export const getCrossSellButtonText = (state) => getCrossSellProductsData(state).buttonText;
export const getCrossSellActiveButtonText = (state) =>
  getCrossSellProductsData(state).activeButtonText;
export const getCrossSellLoadDetailsUrl = (state) => getCrossSellProductsData(state).loadDetailsUrl;
export const getCrossSellShowButtonText = (state) => getCrossSellProductsData(state).showButtonText;

/** Selectors for summary cross sell item list */
export const getCrossSellProductsListRaw = (state) => getCrossSellProductsData(state)?.list || [];
export const getCrossSellProductsList = createSelector(
  getCrossSellProductsListRaw,
  getBasketProductIds,
  (list, addedItems) =>
    list.map(({ shopProductId, productUrl, price, feedback, feedBack, ...otherFields }, index) => ({
      ...otherFields,
      price,
      feedback: feedback || feedBack,
      discount: Math.max(price.oldValue - price.value, 0) || 0,
      position: index + 1,
      isInBasket: addedItems.includes(shopProductId.toString()),
      productId: shopProductId,
      url: productUrl,
    })),
);

export const getCrossSellProductsDict = createSelector(
  getCrossSellProductsList,
  convertListToObjectBy('productId'),
);

export const getCrossSellProductsAvailability = (state) =>
  !!getCrossSellProductsListRaw(state).length;
export const getCrossSellActiveId = (state) => getCrossSellProductsData(state).activeProductId;
export const getCrossSellActivity = (state) => !!getCrossSellActiveId(state);
export const getCrossSellItemsCount = (state) => getCrossSellProductsListRaw(state).length;

/** crossSell in tab */
export const getCrossSellCategorys = (state) => getCrossSellProductsData(state).categorys || [];
export const getCrossSellActiveCategory = (state) =>
  (getCrossSellCategorys(state).filter((item) => item.active)[0] || []).text || '';

export const getCrossSellCountToShowDesktop = (state) =>
  getCrossSellProductsData(state).countToShowDesktop;
export const getCrossSellCountToShowMobile = (state) =>
  getCrossSellProductsData(state).countToShowMobile;
export const getCrossSellProductsListUpToCategory = createSelector(
  getCrossSellProductsList,
  getCrossSellActiveCategory,
  (list, activeCategory) => {
    const filtredList = list.filter((item) => item.category === activeCategory);
    if (filtredList.length > 0) {
      return filtredList;
    }
    return list;
  },
);

export const getCrossSellProductsDictUpToCategory = createSelector(
  getCrossSellProductsListUpToCategory,
  convertListToObjectBy('productId'),
);

/**
 * ratings
 */
export const getCSPFeedback = (state, productId) =>
  (getCrossSellProductsDict(state)[productId] || {}).feedback || {};
export const getCSPRating = (state, productId) => getCSPFeedback(state, productId)?.average || 0;

/**
 * badges
 */

export const getCSPBadges = (state, productId) => {
  return (getCrossSellProductsDict(state)[productId] || {}).badges || [];
};
export const getCSPBadgesList = (state, productId) =>
  getCSPBadges(state, productId).map((badge) => badge.title);

export const getCSPBadgesTitlesString = (state, productId) =>
  getCSPBadgesList(state, productId).reduce(
    (prevString, title) => (prevString ? `${prevString}|${title}` : title),
    '',
  );

/**
 * Selectors for cross sell items
 *
 * composer is used to apply `nestedSelector` to item received by fetching via `productId`
 * all non exported selectors can't be used on their own,
 *   and must be applied to another portion of state via composer
 *   all resulting selectors take (state, { productId }) as parameters
 */
const composeCrossSellItemSelector =
  (nestedSelector) =>
  (state, { productId }) =>
    nestedSelector(getCrossSellProductsDict(state)[productId]);

const getItemRequestStatus = (item) => item?.requestStatus;
const getItemRequestIsBusy = (item) =>
  RequestFiredStatusList.indexOf(getItemRequestStatus(item)) !== -1;
const getItemRequestLoaded = (item) => getItemRequestStatus(item) === REQUEST_SUCCESS;

/**
 * given (`state`, { productId }) returns true if fetch request is in progress or done
 */
export const getCrossSellItemRequestIsBusy = composeCrossSellItemSelector(getItemRequestIsBusy);

export const getCrossSellActiveItem = (state) =>
  getCrossSellProductsDict(state)[getCrossSellActiveId(state)];
/**
 * `CSAI` stands for CrossSellActiveItem
 */

/** CSAI common selectors */
export const getCSAILoaded = (state) => getItemRequestLoaded(getCrossSellActiveItem(state));
export const getCSAIRequestIsBusy = (state) => getItemRequestIsBusy(getCrossSellActiveItem(state));
export const getCSAITitle = (state) => getCrossSellActiveItem(state).title;
export const getCSAIGalleryImages = (state) => getCrossSellActiveItem(state).images;
export const isCSAIInBasket = (state) => getCrossSellActiveItem(state).isInBasket;

export const getCSAIIndex = createSelector(
  getCrossSellProductsList,
  getCrossSellActiveId,
  (list, activeId) => list.findIndex(({ productId }) => productId === activeId),
);
export const isCSAIFirst = (state) => getCSAIIndex(state) <= 0;
export const isCSAILast = (state) => getCSAIIndex(state) >= getCrossSellItemsCount(state) - 1;
export const getCSAIPrevReferenceTemplate = (state) =>
  getCrossSellActiveItem(state).prevReferenceTmpl;
export const getCSAINextReferenceTemplate = (state) =>
  getCrossSellActiveItem(state).nextReferenceTmpl;

/** CSAI compare */
const getCSAICompareDetails = (state) => getCrossSellActiveItem(state).compareDetails;
export const getCSAICompareUrl = (state) => getCSAICompareDetails(state).compareUrl;
export const isCSAICompared = createSelector(
  getComparedProductIds,
  getCrossSellActiveId,
  (comparedProducts, productId) => comparedProducts.indexOf(productId) !== -1,
);

/** CSAI characteristics */
const getCSAICharacteristicsDetails = (state) => getCrossSellActiveItem(state).characteristics;
export const getCSAICharacteristicsUrl = (state) => getCSAICharacteristicsDetails(state).url;
export const getCSAICharacteristicsReferenceTemplate = (state) =>
  getCSAICharacteristicsDetails(state).referenceTemplate;
export const getCSAICharacteristicsBrief = (state) => getCSAICharacteristicsDetails(state).brief;

/** CSAI feedback  */
const getCSAIFeedbackDetails = (state) => getCrossSellActiveItem(state).feedback;
export const getCSAIFeedbackUrl = (state) => getCSAIFeedbackDetails(state).url;
export const getCSAIFeedbackRating = (state) => getCSAIFeedbackDetails(state).average;
export const getCSAIFeedbackCount = (state) => getCSAIFeedbackDetails(state).count;
export const getCSAIFeedbackAvailability = (state) => !!getCSAIFeedbackCount(state);

/** CSAI price */
const getCSAIPriceData = (state) => getCrossSellActiveItem(state).price;
export const getCSAIPrice = (state) => getCSAIPriceData(state).value;
export const getCSAIPriceOld = (state) => getCSAIPriceData(state).oldValue;

/** CSAI credit */
const getCSAICreditDetails = (state) => getCrossSellActiveItem(state).creditDetails;
export const getCSAICreditUrl = (state) => getCSAICreditDetails(state).url;
export const getCSAICreditMinPayment = (state) => getCSAICreditDetails(state).minPayment;
export const getCSAICreditReferenceTemplate = (state) =>
  getCSAICreditDetails(state).referenceTemplate;
export const getCSAICreditAvailability = (state) => getCSAICreditDetails(state).availableCredit;
